import { _, helpers, mui, React, ts, ui } from '_core';

import { Params } from './types';
import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  hasSPPipeline: boolean;
  hasPipeline: boolean;
  fullScreen: boolean;
  params: Params;
  title: string;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  file,
  params,
  hasSPPipeline,
  hasPipeline,
  fullScreen,
  title,
}: ChartProps) => {
  const [data, setData] = React.useState<Record<'raw' | 'pipeline' | 'sp_pipeline', ts.types.widgets.TableData>>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [selectedTab, setSelectedTab] = React.useState<'raw' | 'pipeline' | 'sp_pipeline'>('raw');

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
        },
      });
      if (response) {
        const renamedData = response.data.map((item) => {
          return {
            ..._.omit(item, ['histogram', 'history']),
            category: item.histogram,
          };
        });

        const groupedData = _.groupBy(renamedData, 'histogram_type') as any;
        setData(groupedData);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const getTabs = () => (
    <mui.core.Tabs
      indicatorColor="primary"
      textColor="primary"
      value={selectedTab}
      onChange={(e, newTab) => setSelectedTab(newTab)}
    >
      <mui.core.Tab label="Raw" value="raw" />
      {hasPipeline && <mui.core.Tab label="After Pipeline" value="pipeline" />}
      {hasSPPipeline && <mui.core.Tab label="After Portfolio Pipeline" value="sp_pipeline" />}
    </mui.core.Tabs>
  );

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;
    const nestedBarData = nestedBarChartFormat(data[selectedTab], 'category');
    return nestedBarData;
  }, [data, selectedTab]);

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      {getTabs()}
      <mui.core.Box flexGrow={1}>
        {!_.isNil(formattedData) && (
          <ui.NestedBarChart
            id={id}
            data={formattedData?.data}
            count={formattedData?.count}
            category="category"
            hideFromChart={['min', 'max', 'histogram_type']}
            alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
            fullScreen={fullScreen}
            rotation={90}
            tooltipText={
              params.percent
                ? 'Count: {value}% \n Min: {min} \n Max: {max}'
                : 'Count: {value} \n Min: {min} \n Max: {max}'
            }
            yAxisLabel={params.percent ? 'Count (%)' : 'Count'}
            numberFormat={null}
            exportTitle={title}
            readOnly={readOnly}
            hideLegend
          />
        )}
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
